import { useCallback } from "react";
import { Box, Heading, List, Skeleton } from "@chakra-ui/react";
import { gql } from "__generated__/gql";
import { useQuery } from "utils/apollo/hooks";
import MarkdownText from "components/Shared/MarkdownText";
import { trackEvent } from "utils/segment";
import ExampleDocument from "./ExampleDocument";

const folderQuery = gql(/* GraphQL */ `
  query FolderDetails($slug: String!) {
    documentFolder: documentFolderBySlug(slug: $slug) {
      id
      name
      slug
      description
      exampleDocuments: exampleDocumentsList(orderBy: CREATED_AT_ASC) {
        id
        name
        originalFilename
        description
      }
    }
  }
`);

type Props = {
  slug: string;
};

export default function FolderDetails({ slug }: Props) {
  const { data, loading } = useQuery(folderQuery, {
    variables: { slug },
    returnPartialData: true,
  });
  const { exampleDocuments = [] } = data?.documentFolder || {};

  const handleExampleClick = useCallback(
    (exampleDoc: { id: UUID; name: string }) => {
      trackEvent("Template Downloaded", {
        ...exampleDoc,
        folderId: data?.documentFolder?.id,
        folderName: data?.documentFolder?.name,
        folderSlug: data?.documentFolder?.slug,
      });
    },
    [data?.documentFolder],
  );

  return (
    <>
      <Skeleton isLoaded={!!data?.documentFolder?.name || !loading}>
        <Heading as="h2" size="md" color="magenta.veryDark">
          {data?.documentFolder?.name || " "}
        </Heading>
      </Skeleton>

      <Box marginTop="6">
        <MarkdownText
          isLoaded={!!data?.documentFolder?.description || !loading}
          markdown={data?.documentFolder?.description}
        />
      </Box>

      {exampleDocuments.length > 0 && (
        <Box>
          <Heading as="h3" size="sm" color="magenta.veryDark" marginTop="10">
            Examples &amp; Templates
          </Heading>

          <List>
            {exampleDocuments.map(({ id, ...doc }) => (
              <ExampleDocument
                key={id}
                documentId={id}
                {...doc}
                onClick={handleExampleClick}
              />
            ))}
          </List>
        </Box>
      )}
    </>
  );
}
